import {
  Flex,
  FlexProps,
  ListItem,
  Stack,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'ui-components';
import { AlertModal } from 'ui-components/dist/AlertModal';
import authApi from '~/authApi';
import clientApi from '~/clientApi';
import {
  INVITATIONS_STATUS,
  SNACKBAR_ERROR_GENERIC_OPTIONS,
  USER_ROLES,
} from '~/constants';
import { useAuth } from '~/contexts/auth';
import { fetchCreator } from '~/ducks/creator';
import { captureException } from '~/errorsLogging';
import {
  ACCEPTED_INVITATIONS_COLLECTION_QUERY_KEY,
  INVITATIONS_COLLECTION_QUERY_KEY,
  USER_INVITATION_STATUS_QUERY_KEY,
} from '~/queries/keys';
import { selectCreator } from '~/selectors/creator';
import { ManagerShortType } from '~/types';

import { createEmptyModalData } from './ManagedCreatorInvitationList.types';
import { ManagedCreatorInvitationListItem } from './components';

interface ModalMessageProps {
  currentAgency?: string;
  manager?: string;
  newAgency?: string;
}

export const ManagedCreatorInvitationList: React.FC<FlexProps> = (props) => {
  const queryClient = useQueryClient();
  const [modalData, setModalData] = React.useState(createEmptyModalData());
  const { acceptManagerInvite, redirect } = useAuth();
  const creator = useSelector(selectCreator);
  const dispatch = useDispatch();
  const snackbar = useSnackbar();

  const { data: { results: invitations = [] } = {} } = useQuery(
    [INVITATIONS_COLLECTION_QUERY_KEY],
    () => clientApi.invitations.index(),
    {
      enabled: !authApi.user.roles.includes(USER_ROLES.talentManager),
    },
  );
  const pendingInvitations = invitations.filter(
    (i) => i.status === INVITATIONS_STATUS.pending,
  );
  const acceptedInvitation = invitations.find(
    (i) => i.status === INVITATIONS_STATUS.accepted,
  );

  const acceptInvitation = React.useCallback(
    async (id: string) => {
      try {
        await acceptManagerInvite(id);

        if (creator?.id) {
          const fetchedCreator = await clientApi.creators.read(creator.id);
          dispatch((fetchCreator as any).fulfilled(fetchedCreator));
        }

        redirect();

        snackbar.success({
          description: 'Invitation accepted.',
          duration: 9000,
          isCloseable: true,
        });

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [USER_INVITATION_STATUS_QUERY_KEY],
          }),
          queryClient.invalidateQueries({
            queryKey: [INVITATIONS_COLLECTION_QUERY_KEY],
          }),
          queryClient.invalidateQueries({
            queryKey: [ACCEPTED_INVITATIONS_COLLECTION_QUERY_KEY],
          }),
        ]);
      } catch (error) {
        snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
        captureException(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [acceptManagerInvite, creator?.id, dispatch, snackbar],
  );

  const handleConfirm = (
    invitationId: string,
    manager: ManagerShortType,
    newAgencyName: string,
  ) => {
    if (pendingInvitations.length >= 1 && acceptedInvitation) {
      setModalData({
        invitationId,
        isOpen: true,
        invitationAgencyName: newAgencyName,
        manager,
      });
    } else {
      acceptInvitation(invitationId);
    }
  };

  const handleReject = async (id: string) => {
    try {
      await clientApi.invitations.decline(id);

      snackbar.success({
        description: 'Invitation rejected.',
        duration: 9000,
        isCloseable: true,
      });

      await queryClient.invalidateQueries({
        queryKey: [INVITATIONS_COLLECTION_QUERY_KEY],
      });
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      captureException(error);
    }
  };

  const handleCancelModal = () => {
    setModalData({
      ...modalData,
      isOpen: false,
    });
  };

  const handleConfirmModal = React.useCallback(async () => {
    await acceptInvitation(modalData.invitationId);
    setModalData({
      ...modalData,
      isOpen: false,
    });
  }, [acceptInvitation, modalData]);

  const ModalMessage: React.FC<ModalMessageProps> = ({
    currentAgency,
    manager,
    newAgency,
  }) => {
    if (pendingInvitations.length > 1) {
      return acceptedInvitation ? (
        <>
          <Text mb={4}>
            {manager} from {newAgency} has invited you to connect to their
            account. Connecting will automatically:
            <UnorderedList>
              <ListItem>
                Disconnect you from your current agency, {currentAgency}.
              </ListItem>
              <ListItem> Decline all other pending invitations.</ListItem>
            </UnorderedList>
          </Text>
          Are you ready to connect with the new account?
        </>
      ) : (
        <>
          <Text mb={4}>
            {manager} from {newAgency} has invited you to connect to their
            account. Connecting will automatically decline all other pending
            invitations.
          </Text>
          Are you ready to connect with the new account?
        </>
      );
    } else {
      return (
        <>
          <Text mb={4}>
            {manager} from {newAgency} has invited you to connect to their
            account. Connecting will automatically disconnect you from your
            current agency, {currentAgency}.
          </Text>
          Are you ready to connect with the new account?
        </>
      );
    }
  };

  return (
    <Flex
      position="fixed"
      bottom={0}
      left={0}
      padding={6}
      width="100%"
      pointerEvents="none"
      zIndex="dropdown"
      justifyContent={{
        base: 'center',
        lg: 'flex-start',
      }}
      {...props}
    >
      <Stack _empty={{ marginBottom: 0 }} shouldWrapChildren spacing={4}>
        {pendingInvitations.map(({ id, manager, agencyName }) => (
          <ManagedCreatorInvitationListItem
            id={id}
            key={id}
            manager={manager}
            agencyEntityName={agencyName}
            onConfirm={handleConfirm}
            onReject={handleReject}
          />
        ))}
      </Stack>

      <AlertModal
        isOpen={modalData.isOpen}
        cancelButtonProps={{
          onClick: handleCancelModal,
        }}
        acceptButtonProps={{
          onClick: handleConfirmModal,
          title: 'Connect',
        }}
        title={`Connect with ${modalData.invitationAgencyName}`}
        onClose={handleCancelModal}
      >
        <ModalMessage
          newAgency={modalData.invitationAgencyName}
          currentAgency={acceptedInvitation?.agencyName}
          manager={modalData.manager?.name}
        />
      </AlertModal>
    </Flex>
  );
};
